/* =========================================================
   Boost Blenz — Persistent Sticky ATC
   ---------------------------------------------------------
   This bar is a mirror of the CRO panel. It does NOT own
   add-to-cart logic — that lives in cro-panel.js. We just:
     • keep the bar's display in sync with whichever option
       is selected in the hero (or in our own dropdown)
     • forward bar clicks to the hero's CRO add-to-cart
       button so gift dedupe, cart drawer, etc. run as-is
   ========================================================= */

(function () {
  'use strict';

  function StickyATC(root) {
    this.root = root;

    // Bar elements
    this.thumb         = root.querySelector('[data-bb-sticky-thumb]');
    this.title         = root.querySelector('[data-bb-sticky-title]');
    this.gifts         = root.querySelector('[data-bb-sticky-gifts]');
    this.trigger       = root.querySelector('[data-bb-sticky-trigger]');
    this.triggerTitle  = root.querySelector('[data-bb-sticky-trigger-title]');
    this.triggerSub    = root.querySelector('[data-bb-sticky-trigger-sub]');
    this.menu          = root.querySelector('[data-bb-sticky-menu]');
    this.options       = root.querySelectorAll('[data-bb-sticky-option]');
    this.priceEl       = root.querySelector('[data-bb-sticky-price]');
    this.atcBtn        = root.querySelector('[data-bb-sticky-atc]');

    // Hero CRO panel (source of truth for add-to-cart)
    this.croPanel = document.querySelector('[data-cro-panel]');
    this.croCards = this.croPanel ? this.croPanel.querySelectorAll('[data-cro-bundle-card]') : [];
    this.croATC   = this.croPanel ? this.croPanel.querySelector('[data-cro-add-to-cart]') : null;

    this.bind();
    this.applyBottomPadding();
    this.syncFromHeroInitial();
    this.observeDrawer();
  }

  /* Slide the sticky bar offscreen while any header overlay is open:
       • mobile drawer (observed via class change on [data-bb-drawer])
       • desktop dropdowns / SHOP mega-menu (listen to bb:dropdown events
         dispatched from bb-header.liquid)
     When everything is closed again, bring the bar back. */
  StickyATC.prototype.observeDrawer = function () {
    var self = this;

    // Mobile drawer — class-based, no event hookup in bb-header needed
    var drawer = document.querySelector('[data-bb-drawer]');
    if (drawer) {
      var obs = new MutationObserver(function () {
        self._setHiddenFor('drawer', drawer.classList.contains('is-open'));
      });
      obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    // Desktop dropdowns (SHOP mega, LEARN, HELP & CONTACT)
    document.addEventListener('bb:dropdown:opened', function () {
      self._setHiddenFor('dropdown', true);
    });
    document.addEventListener('bb:dropdown:closed', function () {
      self._setHiddenFor('dropdown', false);
    });
  };

  /* Coalesce multiple "hide me" sources (drawer, dropdown, ...) so closing
     one overlay doesn't reveal the bar while another is still open. */
  StickyATC.prototype._setHiddenFor = function (source, hidden) {
    if (!this._hideSources) this._hideSources = {};
    this._hideSources[source] = !!hidden;
    var anyOpen = Object.keys(this._hideSources).some(function (k) {
      return this._hideSources[k];
    }, this);
    this.root.classList.toggle('is-hidden', anyOpen);
  };

  /* Mirror the initially-selected hero card into the bar.
     Why: cro-panel.js calls its own `select(initialCard)` programmatically
     on DOMContentLoaded — that's a method call, not a click event — so our
     card-click listener never fires for the initial state. If the
     server-rendered defaults disagree with what cro-panel ends up showing
     (or pre_option resolved to nil in liquid), the bar paints empty.
     This reads the source of truth from the DOM. */
  StickyATC.prototype.syncFromHeroInitial = function () {
    // Single-option products (binder, kit) have nothing to switch between.
    // The hero card carries no display data (no data-title/data-image —
    // those only live on our own option rows, which single-option mode
    // doesn't render). Syncing here would overwrite the server-rendered
    // title with empty strings — causing a flash-then-blank.
    if (this.root.classList.contains('bb-sticky-atc--single')) return;
    var self = this;
    function run() {
      if (!self.croCards.length) return;
      var initial = null;
      self.croCards.forEach(function (c) {
        if (c.classList.contains('cro-bundle-card--selected')) initial = c;
      });
      if (!initial) initial = self.croCards[0];
      self.syncFromCard(initial);
      self.markSelectedOption(initial.getAttribute('data-index'));
    }
    // cro-panel.js also defers to DOMContentLoaded; the readyState check
    // covers both `loading` and post-load init paths so we never miss the
    // window where cards exist but haven't been selected yet.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      // Microtask delay so cro-panel.js's own init (defer-loaded) has a
      // chance to apply its class state if it loaded right alongside us.
      setTimeout(run, 0);
    }
  };

  /* ---------- Init / wiring ---------- */
  StickyATC.prototype.bind = function () {
    var self = this;

    // Dropdown trigger
    if (this.trigger) {
      this.trigger.addEventListener('click', function (e) {
        e.preventDefault();
        self.toggleMenu();
      });
    }

    // Option clicks
    this.options.forEach(function (opt) {
      opt.addEventListener('click', function () {
        self.selectIndex(opt.getAttribute('data-index'), { source: 'sticky' });
      });
    });

    // Close menu on outside click / Esc
    document.addEventListener('click', function (e) {
      if (!self.isMenuOpen()) return;
      if (self.root.contains(e.target)) return;
      self.closeMenu();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isMenuOpen()) self.closeMenu();
    });

    // Mirror hero -> sticky: any click on a hero bundle card
    this.croCards.forEach(function (card) {
      card.addEventListener('click', function () {
        // cro-panel sets is-selected synchronously on click, then we read
        var idx = card.getAttribute('data-index');
        self.syncFromCard(card);
        // Keep our menu's selection state aligned even though it's closed
        self.markSelectedOption(idx);
      });
    });

    // ATC — forward to hero
    if (this.atcBtn) {
      this.atcBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (self.atcBtn.classList.contains('is-loading')) return;
        if (!self.croATC) return;
        // Reflect loading state on the bar; cro-panel.js manages its own.
        self.atcBtn.classList.add('is-loading');
        self.croATC.click();
        // Match cro-panel's flow (~600-900ms typical) before clearing
        // optimistic loading state. Don't gate on the cart fetch ourselves.
        setTimeout(function () { self.atcBtn.classList.remove('is-loading'); }, 1500);
      });
    }

    // Recompute bottom padding when the viewport changes
    window.addEventListener('resize', function () { self.applyBottomPadding(); });
  };

  /* ---------- Menu open/close ---------- */
  StickyATC.prototype.isMenuOpen = function () {
    return this.menu && !this.menu.hasAttribute('hidden');
  };
  /* Mobile gate for chat-hide events. Matches the bar's mobile-layout
     breakpoint (920px) — above that, the dropdown is small and floats
     beside the bar, so the chat widget doesn't interfere. */
  StickyATC.prototype._isMobile = function () {
    return window.matchMedia('(max-width: 919px)').matches;
  };
  StickyATC.prototype.openMenu = function () {
    if (!this.menu) return;
    this.menu.removeAttribute('hidden');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
    if (this._isMobile()) {
      document.dispatchEvent(new CustomEvent('bb:sticky:opened'));
    }
  };
  StickyATC.prototype.closeMenu = function () {
    if (!this.menu) return;
    this.menu.setAttribute('hidden', '');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    document.dispatchEvent(new CustomEvent('bb:sticky:closed'));
  };
  StickyATC.prototype.toggleMenu = function () {
    if (this.isMenuOpen()) this.closeMenu(); else this.openMenu();
  };

  /* ---------- Selection ---------- */
  StickyATC.prototype.selectIndex = function (idx, opts) {
    opts = opts || {};
    idx = String(idx);

    // Forward to hero so cro-panel.js does the real selection (also keeps
    // the value stack swap + ATC variant data in sync there).
    if (opts.source === 'sticky') {
      var card = null;
      this.croCards.forEach(function (c) {
        if (c.getAttribute('data-index') === idx) card = c;
      });
      if (card) card.click(); // cro-panel's listener handles the rest; our
                              // own card-click listener will then mirror
                              // attributes back into the bar.
    }

    this.markSelectedOption(idx);
    this.closeMenu();
  };

  StickyATC.prototype.markSelectedOption = function (idx) {
    this.options.forEach(function (opt) {
      var on = opt.getAttribute('data-index') === String(idx);
      opt.classList.toggle('is-selected', on);
      opt.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  };

  /* ---------- Sync display from a hero card ---------- */
  StickyATC.prototype.syncFromCard = function (card) {
    if (!card) return;

    // Find our matching option row (same index) — has the richest data
    // (image URLs, escaped title, etc.) without us having to re-query
    // the hero card's child nodes.
    var idx = card.getAttribute('data-index');
    var ours = null;
    this.options.forEach(function (o) {
      if (o.getAttribute('data-index') === idx) ours = o;
    });
    var src = ours || card; // graceful fallback

    var title    = src.getAttribute('data-title') || '';
    var subtitle = src.getAttribute('data-subtitle') || '';
    var price    = card.getAttribute('data-price') || src.getAttribute('data-price') || '';
    var gifts    = card.getAttribute('data-gifts') || '[]';
    var image    = src.getAttribute('data-image') || '';
    var image2x  = src.getAttribute('data-image-2x') || image;

    // Title + image
    if (this.title) this.title.textContent = title;
    if (this.thumb && image) {
      this.thumb.src = image;
      if (image2x) this.thumb.setAttribute('srcset', image + ' 1x, ' + image2x + ' 2x');
      this.thumb.alt = title;
    }

    // Dropdown trigger
    if (this.triggerTitle) this.triggerTitle.textContent = title;
    if (this.triggerSub) this.triggerSub.textContent = subtitle;

    // ATC price
    if (this.priceEl) this.priceEl.textContent = price;

    // Free Gifts tag — show if data-gifts has at least one numeric id
    if (this.gifts) {
      var hasGifts = false;
      try {
        var arr = JSON.parse(gifts);
        hasGifts = Array.isArray(arr) && arr.some(function (g) { return typeof g === 'number' && g > 0; });
      } catch (e) { /* keep hasGifts=false */ }
      this.gifts.classList.toggle('is-hidden', !hasGifts);
    }
  };

  /* ---------- Bottom padding so content isn't covered ---------- */
  StickyATC.prototype.applyBottomPadding = function () {
    if (!this.root) return;
    // Use rAF so we measure after the bar is in the layout
    var self = this;
    requestAnimationFrame(function () {
      var h = self.root.offsetHeight;
      if (!h) return;
      document.documentElement.style.setProperty('--bb-sticky-atc-h', h + 'px');
      // Apply to body — covers cases where templates have varying footers
      document.body.style.paddingBottom = 'calc(' + h + 'px + env(safe-area-inset-bottom, 0px))';
    });
  };

  /* ---------- Boot ---------- */
  function init() {
    document.querySelectorAll('[data-bb-sticky]').forEach(function (el) {
      if (el.__bbStickyInit) return;
      el.__bbStickyInit = true;
      new StickyATC(el);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
